import { forwardRef, type ImgHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';
import lockupOnDarkUrl from '@/shared/assets/brand/workerize-horizontal-lockup.svg';
import lockupOnLightUrl from '@/shared/assets/brand/workerize-horizontal-lockup-onlight.svg';

/**
 * Background the lockup sits on. The wordmark/strokes are baked into the SVG, so
 * the asset (not CSS) carries the colour: `onDark` = near-white wordmark for
 * Ink surfaces, `onLight` = Ink wordmark for Bone surfaces. The amber spark
 * stays constant across both.
 */
export type LockupTone = 'onLight' | 'onDark';

export interface WorkerizeLockupProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  height?: number;
  title?: string;
  tone?: LockupTone;
}

const toneSrc: Record<LockupTone, string> = {
  onLight: lockupOnLightUrl,
  onDark: lockupOnDarkUrl,
};

/**
 * The canonical Workerize horizontal lockup (mark + wordmark). One file per
 * logical asset (DEC-040). The two `tone` variants are the same artwork
 * recoloured for the surface they sit on — for a surface that flips with the
 * theme, use {@link WorkerizeLockupThemed}.
 */
export const WorkerizeLockup = forwardRef<HTMLImageElement, WorkerizeLockupProps>(
  function WorkerizeLockup(
    { height = 56, title = 'Workerize', tone = 'onDark', className, ...rest },
    ref,
  ) {
    return (
      <img
        ref={ref}
        src={toneSrc[tone]}
        alt={title}
        height={height}
        className={cn('inline-block h-auto w-auto max-w-full', className)}
        style={{ height: `${height}px` }}
        {...rest}
      />
    );
  },
);
